'use client';

import { Auth } from './config';
import { 
  PhoneAuthProvider, 
  signInWithCredential, 
  signOut as firebaseSignOut, 
  RecaptchaVerifier,
  signInWithPhoneNumber
} from 'firebase/auth';

interface AuthError extends Error {
  code?: string;
  message: string;
}

class AuthService {
  private recaptchaVerifier: RecaptchaVerifier | null = null;
  private recaptchaWidgetId: number | null = null;

  /**
   * Initialize recaptcha verifier for phone auth
   */
  initRecaptcha(containerId: string) {
    if (typeof window !== 'undefined') {
      try {
        // Clean up any existing recaptcha if it exists
        this.cleanupRecaptcha();
        
        // Check if containerId element exists
        const containerElement = document.getElementById(containerId);
        if (!containerElement) {
          console.error(`Container element with ID '${containerId}' not found`);
          return;
        }
        
        // Create a new RecaptchaVerifier with explicit container and render immediately
        this.recaptchaVerifier = new RecaptchaVerifier(
          Auth,
          containerElement, 
          {
            size: 'invisible',
            callback: () => {
              // reCAPTCHA solved, allow signInWithPhoneNumber.
              console.log('reCAPTCHA verified successfully');
            },
            'expired-callback': () => {
              // Response expired. Ask user to solve reCAPTCHA again.
              console.log('reCAPTCHA expired');
              this.cleanupRecaptcha();
            }
          }
        );
        
        // Render the reCAPTCHA explicitly
        this.recaptchaVerifier.render()
          .then(widgetId => {
            this.recaptchaWidgetId = widgetId;
            console.log('reCAPTCHA rendered successfully');
          })
          .catch(error => {
            console.error('Error rendering reCAPTCHA:', error);
          });
      } catch (error) {
        console.error('Error initializing recaptcha:', error);
        throw error;
      }
    }
  }

  /**
   * Clean up recaptcha verifier
   */
  cleanupRecaptcha() {
    if (this.recaptchaVerifier) {
      try {
        this.recaptchaVerifier.clear();
      } catch (error) {
        console.warn('Error clearing reCAPTCHA:', error);
      }
      this.recaptchaVerifier = null;
      this.recaptchaWidgetId = null;
    }
    
    // Also remove any recaptcha iframes from previous attempts
    if (typeof document !== 'undefined') {
      try {
        // Remove the container elements
        const recaptchaElements = document.querySelectorAll('.firebase-recaptcha');
        recaptchaElements.forEach(el => {
          while (el.firstChild) {
            el.removeChild(el.firstChild);
          }
        });
        
        // Remove any reCAPTCHA iframes that might be left
        const recaptchaIframes = document.querySelectorAll('iframe[src*="recaptcha"]');
        recaptchaIframes.forEach(iframe => iframe.remove());
        
        // Remove any reCAPTCHA scripts
        const recaptchaScripts = document.querySelectorAll('script[src*="recaptcha"]');
        recaptchaScripts.forEach(script => script.remove());
        
        // Remove any reCAPTCHA badges
        const recaptchaBadges = document.querySelectorAll('.grecaptcha-badge');
        recaptchaBadges.forEach(badge => badge.remove());
      } catch (error) {
        console.warn('Error cleaning up reCAPTCHA elements:', error);
      }
    }
  }

  /**
   * Send OTP to phone number
   */
  async sendOtp(phoneNumber: string): Promise<string> {
    try {
      if (!this.recaptchaVerifier) {
        console.warn('Recaptcha not initialized, attempting to initialize now');
        // Try to initialize recaptcha if not already done
        this.initRecaptcha('recaptcha-container');
        
        // Wait a moment for recaptcha to initialize
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        if (!this.recaptchaVerifier) {
          throw new Error('Failed to initialize recaptcha');
        }
      }

      console.log('Sending OTP to:', phoneNumber);
      const confirmationResult = await signInWithPhoneNumber(
        Auth, 
        phoneNumber, 
        this.recaptchaVerifier
      );
      
      console.log('OTP sent successfully');
      return confirmationResult.verificationId;
    } catch (error: any) {
      console.error('Error sending OTP:', error);
      // Reset recaptcha on error
      this.cleanupRecaptcha();
      throw error;
    }
  }

  /**
   * Verify OTP and sign in
   */
  async verifyOtp(verificationId: string, otp: string) {
    try {
      const credential = PhoneAuthProvider.credential(verificationId, otp);
      const userCredential = await signInWithCredential(Auth, credential);
      return userCredential.user;
    } catch (error: any) {
      console.error('Error verifying OTP:', error);
      throw error;
    } finally {
      this.cleanupRecaptcha();
    }
  }

  /**
   * Signs out the user and cleans up all auth-related data
   */
  async signOut(): Promise<void> {
    try {
      await firebaseSignOut(Auth);
      
      // Clear all auth traces
      if (typeof document !== 'undefined') {
        document.cookie = 'session=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT';
        document.cookie = 'firebase-token=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT';
        document.cookie = 'user-phone=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT';
        
        localStorage.removeItem('authUser');
      }
    } catch (error) {
      console.error('Error during sign out:', error);
      throw error;
    }
  }

  /**
   * Checks if user is currently authenticated
   */
  isAuthenticated(): boolean {
    return !!Auth.currentUser;
  }

  /**
   * Gets the current user's ID token
   */
  async getCurrentUserToken(): Promise<string | null> {
    try {
      const user = Auth.currentUser;
      if (!user) return null;
      
      return await user.getIdToken(true);
    } catch (error) {
      console.error('Error getting user token:', error);
      return null;
    }
  }

  /**
   * Gets current Firebase user if logged in
   */
  getCurrentUser() {
    return Auth.currentUser;
  }

  /**
   * Checks if auth token is expired and needs refresh
   */
  async isTokenExpired(): Promise<boolean> {
    try {
      const user = Auth.currentUser;
      if (!user) return true;

      // Get token result which includes expiration
      const tokenResult = await user.getIdTokenResult();
      const expirationTime = new Date(tokenResult.expirationTime).getTime();
      const now = Date.now();

      // Token is expired if current time is past expiration
      return now >= expirationTime;
    } catch (error) {
      console.error('Error checking token expiration:', error);
      return true;
    }
  }
}

export const authService = new AuthService();
